import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
  },
});

/** Width preset for the sliding blade. */
export type BladeSize = 'small' | 'medium' | 'large';

export interface BladeProps {
  /** Whether the blade is open. Controlled by the consumer. */
  open: boolean;
  /** Called when the blade requests to close (X, Esc, or overlay click). */
  onClose: () => void;
  /** Blade title shown in the header. */
  title: ReactNode;
  /** Blade content. */
  children: ReactNode;
  /** Width preset; defaults to `medium`. */
  size?: BladeSize;
}

/**
 * Right-sliding blade panel — the portal.azure.com primitive for a focused
 * detail/create surface over the current page. Wraps Fluent's
 * {@link OverlayDrawer} (position `end`) and provides a titled header with a
 * close affordance. Esc-to-close and overlay light-dismiss are handled by the
 * underlying drawer and routed through {@link BladeProps.onClose}. Consumed by
 * Dev-D's tenant create/detail blades.
 */
export function Blade({
  open,
  onClose,
  title,
  children,
  size = 'medium',
}: BladeProps): JSX.Element {
  const styles = useStyles();

  return (
    <OverlayDrawer
      open={open}
      position="end"
      size={size}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close panel"
              icon={<DismissRegular />}
              onClick={onClose}
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>{children}</div>
      </DrawerBody>
    </OverlayDrawer>
  );
}
